import { AfterViewChecked, Component, ElementRef, Input, OnChanges, OnDestroy } from '@angular/core';
import { combineLatest, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap, take, timeout } from 'rxjs/operators';
import { StreakerCentralStateService } from '../../streaker-central/services/streaker-central-state.service';
import { FitnessMediaService } from '../../shared/services/fitness-media.service';
import { userProfilePath, teamProfilePath } from '../../shared/services/profile-routing/profile-links';
import { BadgeService } from '../services/badge.service';
import { BadgeBoard, badgeScore } from '../models/badge.models';
import { ActivityBoards, FullStanding, MetricBoard, OwnFitnessStats, activityGroups, visibleActivities,
    BoardAudience, HeaderBadge, leaderPercent, standingScore } from '../models/leaderboard.models';

@Component({selector: 'app-badge-leaderboards', templateUrl: './badge-leaderboards.component.html',
    styleUrls: ['./badge-leaderboards.component.scss']})
export class BadgeLeaderboardsComponent implements OnDestroy, OnChanges, AfterViewChecked {
    @Input() embedded = false;
    @Input() teamView = false;
    private _reload = new Subject<void>();
    private _reads = new Subscription();
    private _boards = new Subscription();
    private _metricReads = new Map<MetricBoard, Subscription>();
    private _searchTimers = new Map<MetricBoard, Subscription>();
    private _catalog: BadgeBoard[] = [];
    private _ownIds: number[] = [];
    private _scope = '';
    private _userId = 0;
    private _pins: number[] = [];
    private _focusRow = '';
    private _focusSearch = '';
    private _headerBadges: HeaderBadge[] = [];
    private _avatarFallbacks = new WeakMap<FullStanding, string>();
    challengeId = 0;
    challengeName = '';
    teams = false;
    loading = true;
    error = '';
    pending = false;
    ownKnown = false;
    ownError = false;
    showAll = false;
    openActivity = 0;
    groups: ActivityBoards[] = [];
    readonly score = standingScore;
    readonly percent = leaderPercent;
    readonly badgeScore = badgeScore;
    constructor(public state: StreakerCentralStateService, private service: BadgeService, private media: FitnessMediaService,
        private element: ElementRef<HTMLElement>) {
        this._reads.add(combineLatest([
            state.state$.pipe(map(s => ({challengeId: s.challenge?.ChallengeDefinitionId || 0,
                name: s.challenge?.Name || '', userId: s.userId, enrollmentId: s.participant?.UserChallengeId || 0,
                teams: (s.teams?.data || []).map(t => ({id: t.ChallengeTeamId, name: t.TeamName})),
                loading: s.loading, error: s.error})), distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))),
            service.changed$.pipe(startWith(null)), this._reload.pipe(startWith(null))
        ]).pipe(switchMap(([context]) => {
            this._boards.unsubscribe(); this._boards = new Subscription();
            this._metricReads.clear();
            this._searchTimers.clear();
            const scope = JSON.stringify([context.userId, context.challengeId, context.enrollmentId]);
            if (scope !== this._scope) {
                this._scope = scope; this.showAll = false; this.openActivity = 0; this.teams = this.embedded ? this.teamView : false;
            }
            this._userId = context.userId; this.challengeId = context.challengeId; this.challengeName = context.name;
            this.loading = true; this.error = ''; this.groups = []; this._catalog = []; this.pending = false;
            this._avatarFallbacks = new WeakMap<FullStanding, string>();
            this._headerBadges = []; this._focusRow = ''; this._focusSearch = '';
            if (!context.challengeId || context.loading || context.error) {
                this.loading = context.loading; this.error = context.error; return of(null);
            }
            const own = context.enrollmentId ? service.ownStats(context.challengeId, context.enrollmentId).pipe(
                map(stats => ({stats, failed: false})), catchError(() => of({stats: null as OwnFitnessStats, failed: true})))
                : of({stats: null as OwnFitnessStats, failed: false});
            const badgeSources = [
                ...(context.enrollmentId ? [{id: context.enrollmentId, team: false, name: ''}] : []),
                ...context.teams.map(t => ({id: t.id, team: true, name: t.name}))
            ].map(subject => service.collection(context.challengeId, subject.team ? undefined : subject.id, subject.team ? subject.id : undefined)
                .pipe(take(1), map(collection => collection.Badges.map(badge => ({badge, subjectId: subject.id, team: subject.team, name: subject.name}))),
                    catchError(() => of([] as HeaderBadge[]))));
            const badges = badgeSources.length ? combineLatest(badgeSources).pipe(take(1), map(lists => ([] as HeaderBadge[]).concat(...lists))) : of([] as HeaderBadge[]);
            // Header awards are optional decoration; do not hold standings behind every team's badge collection.
            this._boards.add(badges.subscribe(awards => this._headerBadges = awards));
            return combineLatest([service.boards(context.challengeId).pipe(timeout(30000), take(1)), own]).pipe(take(1),
                map(([catalog, personal]) => ({catalog, personal})),
                catchError(() => { this.error = 'Unable to load leaderboards. Please try again.'; this.loading = false; return of(null); }));
        })).subscribe(result => {
            if (!result) { return; }
            this.loading = false; this.pending = result.catalog.State !== 'ready'; this._catalog = result.catalog.Boards;
            if (this._catalog.some(board => !board.FitnessActivityTypeId || typeof board.IsRanked !== 'boolean')) {
                this.error = 'Leaderboards are being updated. Please refresh shortly.'; return;
            }
            this.ownError = result.personal.failed; this.ownKnown = result.personal.stats?.State === 'ready';
            this._ownIds = (result.personal.stats?.Activities || []).filter(a => a.TotalActivities > 0).map(a => a.FitnessActivityTypeId);
            this.rebuildGroups();
        }));
    }
    get visible(): ActivityBoards[] { return visibleActivities(this.groups, this.showAll, this.ownKnown); }
    ngOnChanges(): void {
        if (!this.embedded) { return; }
        if (this.loading) { this.teams = this.teamView; } else { this.setTeams(this.teamView); }
    }
    get hiddenCount(): number { return this.groups.length - this.visible.length; }
    get preferenceKey(): string { return 'fitness.leaderboards.activities.v1:' + this._userId + ':' + this.challengeId + ':' + (this.teams ? 'teams' : 'people'); }
    private readPins(): number[] {
        try {
            const value: unknown = JSON.parse(localStorage.getItem(this.preferenceKey) || '[]');
            return Array.isArray(value) ? value.filter(id => typeof id === 'number' && Number.isSafeInteger(id) && id > 0) : [];
        } catch { return []; }
    }
    private rebuildGroups(): void {
        this._pins = this.readPins(); this.groups = activityGroups(this._catalog, this.teams, this._ownIds, this._pins);
        if (!this.visible.some(g => g.id === this.openActivity)) { this.openActivity = this.visible[0]?.id || 0; }
        const open = this.visible.find(g => g.id === this.openActivity); if (open) { this.loadActivity(open); }
    }
    setTeams(teams: boolean): void {
        if (this.loading || this.teams === teams) { return; }
        this.teams = teams; this.openActivity = 0;
        this._boards.unsubscribe(); this._boards = new Subscription(); this._metricReads.clear(); this._searchTimers.clear(); this.rebuildGroups();
    }
    toggleAll(): void {
        this.showAll = !this.showAll;
        if (!this.visible.some(g => g.id === this.openActivity)) { this.openActivity = this.visible[0]?.id || 0; }
        const open = this.visible.find(g => g.id === this.openActivity); if (open) { this.loadActivity(open); }
    }
    toggleActivity(group: ActivityBoards): void {
        this.openActivity = this.openActivity === group.id ? 0 : group.id;
        if (this.openActivity) { this.loadActivity(group); }
    }
    pin(group: ActivityBoards): void {
        group.pinned = !group.pinned; this._pins = this.groups.filter(g => g.pinned).map(g => g.id);
        try { localStorage.setItem(this.preferenceKey, JSON.stringify(this._pins)); } catch { /* Pins still work in memory. */ }
        this.groups.sort((a, b) => Number(b.pinned) - Number(a.pinned) || a.name.localeCompare(b.name) || a.id - b.id);
        if (!this.visible.some(g => g.id === this.openActivity)) { this.openActivity = this.visible[0]?.id || 0; }
        const open = this.visible.find(g => g.id === this.openActivity); if (open) { this.loadActivity(open); }
    }
    private loadActivity(group: ActivityBoards): void {
        group.metrics.filter(m => !m.loading && (!m.data || Date.now() >= (m.expiresAt || 0))).forEach(m => this.load(m));
    }
    load(metric: MetricBoard, page = 1, search = metric.appliedSearch, focusId?: number): void {
        this._searchTimers.get(metric)?.unsubscribe(); this._searchTimers.delete(metric);
        this._metricReads.get(metric)?.unsubscribe();
        metric.loading = true; metric.error = '';
        metric.focusId = undefined;
        const request = this.service.fullStandings(this.challengeId, metric.board.ChallengeBadgeRuleId, page, search,
            metric.data?.PublicationId, metric.audience, focusId).subscribe(data => {
            metric.loading = false; metric.data = data;
            metric.expiresAt = data.State === 'ready' ? data.cacheExpiresAt || Date.now() + 300000 : 0;
            if (data.Leaders && data.State === 'ready') { metric.leaders = data.Leaders; }
            if (data.Page === 1 && !search && metric.audience === 'all' && !focusId && data.State === 'ready') { metric.leaders = data.Rows.slice(0, 3); }
            metric.appliedSearch = search;
            if (focusId && data.State === 'ready') {
                const row = data.Rows.find(r => (r.UserChallengeId || r.ChallengeTeamId) === focusId);
                if (row && this.openActivity === metric.board.FitnessActivityTypeId && metric.expanded) {
                    metric.focusId = focusId; this._focusRow = this.rowId(metric, row);
                }
            }
        }, () => { metric.loading = false; metric.error = 'Unable to load this leaderboard. Please try again.'; });
        this._metricReads.set(metric, request); this._boards.add(request);
    }
    search(metric: MetricBoard): void { this.load(metric, 1, metric.search.trim()); }
    searchChanged(metric: MetricBoard, text: string): void {
        metric.search = text;
        this._searchTimers.get(metric)?.unsubscribe();
        const pending = timer(300).subscribe(() => this.search(metric));
        this._searchTimers.set(metric, pending); this._boards.add(pending);
    }
    toggleSearch(metric: MetricBoard): void {
        metric.searchVisible = !metric.searchVisible;
        if (metric.searchVisible) { this._focusSearch = 'search-' + metric.board.ChallengeBadgeRuleId; }
        if (!metric.searchVisible) { metric.search = ''; metric.audience = 'all'; this.search(metric); }
    }
    audienceChanged(metric: MetricBoard, audience: BoardAudience = metric.audience): void {
        metric.audience = audience; this.search(metric);
    }
    awards(group: ActivityBoards): HeaderBadge[] {
        return this._headerBadges.filter(a => a.team === this.teams && group.metrics.some(m => m.board.ChallengeBadgeRuleId === a.badge.ChallengeBadgeRuleId));
    }
    jumpToBadge(group: ActivityBoards, award: HeaderBadge): void {
        const metric = group.metrics.find(m => m.board.ChallengeBadgeRuleId === award.badge.ChallengeBadgeRuleId);
        if (!metric) { return; }
        this.openActivity = group.id; metric.expanded = true; metric.searchVisible = false;
        metric.search = ''; metric.appliedSearch = ''; metric.audience = 'all';
        this.load(metric, 1, '', award.subjectId); this.loadActivity(group);
    }
    rowId(metric: MetricBoard, row: FullStanding): string { return 'standing-' + metric.board.ChallengeBadgeRuleId + '-' + (row.UserChallengeId || row.ChallengeTeamId); }
    ngAfterViewChecked(): void {
        if (this._focusSearch) {
            const input = this.element.nativeElement.querySelector<HTMLElement>('#' + this._focusSearch);
            if (input) { this._focusSearch = ''; input.focus(); }
        }
        if (!this._focusRow) { return; }
        const row = this.element.nativeElement.querySelector<HTMLElement>('#' + this._focusRow);
        if (row) { this._focusRow = ''; row.scrollIntoView({block: 'center', behavior: 'auto'}); row.focus({preventScroll: true}); }
    }
    toggleMetric(metric: MetricBoard): void { metric.expanded = !metric.expanded; }
    refresh(): void { this.service.expireCollections(); this._reload.next(); }
    avatar(row: FullStanding): string {
        return this._avatarFallbacks.has(row) ? this._avatarFallbacks.get(row)
            : this.media.urlFromPath(row.AvatarMediaPath, 80, 80);
    }
    avatarFailed(row: FullStanding): void {
        const current = this.avatar(row);
        const fallback = !this._avatarFallbacks.has(row) && /^prod-group\d+-folder\/[^/]+$/.test(row.AvatarMediaPath || '')
            ? this.media.activityPhotoUrl({ _itemName: 'dw_roMedia', MediaPath: row.AvatarMediaPath }, 80) : '';
        this._avatarFallbacks.set(row, fallback && fallback !== current ? fallback : '');
    }
    initials(name: string): string { return (name || 'S').trim().split(/\s+/).slice(0, 2).map(n => n.charAt(0)).join('').toUpperCase(); }
    link(row: FullStanding): string {
        try { return row.UserChallengeId ? row.ProfileSlug ? userProfilePath(row.ProfileSlug) : null
            : row.TeamSlug ? teamProfilePath(this.challengeId, row.TeamSlug) : null; } catch { return null; }
    }
    isMe(row: FullStanding, metric: MetricBoard): boolean {
        const mine = metric.data?.MyStanding;
        return row.IsMe || !!mine && (row.UserChallengeId ? mine.UserChallengeId === row.UserChallengeId : mine.ChallengeTeamId === row.ChallengeTeamId);
    }
    trackActivity(index: number, group: ActivityBoards): number { return group.id; }
    trackMetric(index: number, metric: MetricBoard): number { return metric.board.ChallengeBadgeRuleId; }
    ngOnDestroy(): void { this._reads.unsubscribe(); this._boards.unsubscribe(); this._reload.complete(); }
}
